import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Navbar, Footer } from "../components/Layout";
import { Crown, Check, Sparkles, Lock, Loader2, ArrowRight, Flame, Users } from "lucide-react";
import { toast } from "sonner";
import { useSEO } from "../hooks/useSEO";

const PERKS = [
  { icon: "♾️", title: "Accès à vie", desc: "Pas d'abonnement. Pas de renouvellement. Une fois, pour toujours." },
  { icon: "👑", title: "Badge Fondateur", desc: "Apparaît sur votre profil + dans le club privé des 100 premiers." },
  { icon: "🚀", title: "Tous les futurs cours offerts", desc: "Vous payez aujourd'hui ce que j'ajouterai demain. Les cinq niveaux JLPT, du N5 au N1, enrichis en continu, plus le kanji JLPT et l'audio natif : tout est inclus." },
  { icon: "🎙️", title: "Audio natif premium (à venir)", desc: "Voix japonaises ElevenLabs sur chaque mot et dialogue." },
  { icon: "🧠", title: "Sensei IA illimité", desc: "Claude Sonnet, 24/7, sans quota." },
  { icon: "📊", title: "Roadmap influencée", desc: "Votez pour les prochaines fonctionnalités. Un fondateur = une voix." },
];

// SOCIAL_PROOF hardcodé retiré : jamais de faux témoignages.
// La section "premiers fondateurs" ne s'affiche que quand il y a de vrais
// inscrits (status.slots_used > 0) et qu'il existe de vrais avis en base.

const Fondateur = () => {
  const { user, refresh } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  useSEO({
    title: "Offre Fondateur : Accès à vie 49 € (100 places) | Japonais Pas à Pas",
    description: "Offre limitée aux 100 premiers étudiants : accès à vie à Japonais Pas à Pas pour 49 € (au lieu de 199 €). Toutes les leçons, le tuteur IA et les futures mises à jour incluses.",
    canonical: "https://japonais-pas-a-pas.com/fondateur",
  });

  useEffect(() => {
    api.get("/fondateur/status").then((r) => setStatus(r.data)).catch(() => {});
  }, []);

  const claim = async () => {
    if (!user) {
      toast.info("Créez votre compte (essai 3j gratuit), puis revenez ici.");
      nav("/register");
      return;
    }
    if (user.is_lifetime || user.is_founder) {
      toast.info("Vous avez déjà un accès à vie 🎉");
      return;
    }
    if (!status?.available) {
      toast.error("Toutes les places fondateur ont été prises.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/checkout/session", {
        plan_id: "lifetime",
        origin_url: window.location.origin,
        promo_code: "FONDATEUR50",
      });
      window.location.href = data.url;
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail === "founder_sold_out") {
        toast.error("Trop tard, plus aucune place fondateur.");
        const { data } = await api.get("/fondateur/status");
        setStatus(data);
      } else {
        toast.error(detail || "Erreur de paiement");
      }
      setLoading(false);
    }
  };

  const remaining = status?.slots_remaining ?? 100;
  const pct = status ? Math.round(((status.slots_used || 0) / status.slots_total) * 100) : 0;

  return (
    <div className="min-h-screen bg-[#1A2A40] text-white" data-testid="fondateur-page">
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute -right-20 -top-20 w-[500px] h-[500px] rounded-full bg-[#E94A3F]/20 blur-3xl" />
        <div className="absolute -left-32 top-1/3 w-96 h-96 rounded-full bg-[#FFD166]/15 blur-3xl" />
        <div className="absolute inset-0 opacity-[0.04]" style={{
          backgroundImage: "radial-gradient(rgba(255,255,255,0.6) 1px, transparent 1px)",
          backgroundSize: "32px 32px",
        }} />
        <div className="relative max-w-6xl mx-auto px-6 sm:px-12 pt-12 pb-24">
          <div className="inline-flex items-center gap-2 bg-[#FFD166] text-[#1A2A40] rounded-full px-4 py-1.5 mb-8 font-bold text-xs uppercase tracking-[0.2em] border-2 border-[#1A2A40]">
            <Flame className="w-3.5 h-3.5" /> Offre Fondateur · 100 places · Une seule fois
          </div>

          <h1 className="font-display font-black text-[clamp(2.5rem,7vw,6rem)] leading-[0.95] tracking-tighter mb-6">
            <span className="font-jp text-[#E94A3F]">創立者</span><br />
            Devenez l'un des<br />
            <span className="text-[#FFD166]">100 fondateurs.</span>
          </h1>

          <p className="text-xl text-white/80 max-w-2xl mb-10 leading-relaxed">
            Un accès à vie à Japonais Pas à Pas : leçons, IA, JLPT, SRS, et <em>tout ce que j'ajouterai dans les années à venir</em>.
            <strong className="text-white"> 49 € une seule fois</strong>, au lieu de 199 €.
            Une fois les 100 places parties, l'offre disparaît. Pour de bon.
          </p>

          {/* Price + scarcity */}
          <div className="grid sm:grid-cols-2 gap-4 max-w-2xl mb-10">
            <div className="bg-white/5 backdrop-blur-xl border-2 border-white/10 rounded-2xl p-6">
              <div className="text-xs uppercase tracking-widest text-[#FFD166] font-bold mb-2">Prix fondateur</div>
              <div className="font-display font-black text-5xl mb-1">
                49<span className="text-2xl text-white/60"> €</span>
              </div>
              <div className="text-sm text-white/60 line-through">au lieu de 199 €</div>
              <div className="text-xs text-[#8CA07A] font-bold mt-1">-75% · une seule fois</div>
            </div>
            <div className="bg-[#E94A3F] border-2 border-[#E94A3F] rounded-2xl p-6">
              <div className="text-xs uppercase tracking-widest text-[#FFD166] font-bold mb-2 flex items-center gap-1.5">
                <Users className="w-3 h-3" /> Places restantes
              </div>
              {status ? (
                <>
                  <div className="font-display font-black text-5xl mb-2" data-testid="slots-remaining">{remaining}<span className="text-2xl text-white/70">/100</span></div>
                  <div className="h-2 bg-white/20 rounded-full overflow-hidden">
                    <div className="h-full bg-[#FFD166] transition-all" style={{ width: `${pct}%` }} />
                  </div>
                  {status.slots_used > 0 ? (
                    <div className="text-xs text-white/80 mt-2">
                      {status.slots_used} fondateur{status.slots_used > 1 ? "s" : ""} déjà inscrit{status.slots_used > 1 ? "s" : ""}
                    </div>
                  ) : (
                    <div className="text-xs text-white/60 mt-2 italic">
                      Vous seriez parmi les tout premiers fondateurs.
                    </div>
                  )}
                </>
              ) : (
                <Loader2 className="w-6 h-6 animate-spin" />
              )}
            </div>
          </div>

          {/* CTA */}
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={claim}
              disabled={loading || (status && !status.available) || user?.is_lifetime}
              data-testid="founder-cta"
              className="bg-[#FFD166] text-[#1A2A40] font-bold py-5 px-8 rounded-xl shadow-[6px_6px_0_0_#E94A3F] hover-lift inline-flex items-center justify-center gap-2 text-base disabled:opacity-50"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : (
                <>
                  {user?.is_founder ? "Déjà fondateur 👑" : user?.is_lifetime ? "Déjà à vie 🎉" : status?.available ? "Devenir fondateur, 49 €" : "Places épuisées"} <ArrowRight className="w-5 h-5" />
                </>
              )}
            </button>
            <div className="text-sm text-white/60 self-center max-w-md">
              Paiement unique. Sans abonnement. Remboursé 14j sans question.
            </div>
          </div>
        </div>
      </section>

      {/* PERKS */}
      <section className="bg-[#FDFBF7] text-[#1A2A40] py-24" data-testid="perks-section">
        <div className="max-w-6xl mx-auto px-6 sm:px-12">
          <div className="grid lg:grid-cols-12 gap-10 mb-14">
            <div className="lg:col-span-5">
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#E94A3F] mb-3">Ce que vous obtenez</div>
              <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tighter leading-[1]">
                Plus qu'un accès.<br /><span className="italic font-light">Un statut.</span>
              </h2>
            </div>
            <p className="lg:col-span-6 lg:col-start-7 text-lg text-[#4A5568] leading-relaxed self-end">
              Vous payez aujourd'hui ce que je vais construire pendant les 5 prochaines années.
              Vous n'aurez plus jamais à sortir la carte.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {PERKS.map((p, i) => (
              <div key={i} className="bg-white rounded-2xl p-7 border-2 border-[#1A2A40]/10 hover-lift" data-testid={`perk-${i}`}>
                <div className="text-4xl mb-4">{p.icon}</div>
                <div className="font-display font-bold text-xl mb-2 text-[#1A2A40]">{p.title}</div>
                <p className="text-sm text-[#4A5568] leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SOCIAL PROOF — masqué tant qu'il n'y a aucun inscrit / avis */}
      {/* Aucun faux témoignage : cette section reviendra automatiquement quand
          il y aura de vrais avis à afficher (via /api/testimonials). */}

      {/* FAQ */}
      <section className="bg-[#FDFBF7] text-[#1A2A40] py-24">
        <div className="max-w-3xl mx-auto px-6 sm:px-12">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#E94A3F] mb-3">Questions</div>
          <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tighter leading-[1] mb-10">
            La vérité, sans détour.
          </h2>
          <div className="space-y-5">
            {[
              ["Pourquoi 49 € au lieu de 199 € ?",
               "Parce que vous prenez un risque que les autres ne prendront pas. Vous me faites confiance avant que tout le monde soit là. C'est juste que je vous le rende."],
              ["Que se passe-t-il quand les 100 places sont parties ?",
               "L'offre disparaît. Plus jamais. Les prochains paieront 199 € minimum, et probablement plus quand le catalogue grossira."],
              ["Et si vous arrêtez le projet ?",
               "Je vous rembourse intégralement, sans question. Pas d'embrouille. Mais ce n'est pas mon plan : j'investis personnellement dans le développement."],
              ["Le contenu est-il déjà complet ?",
               "Non, et c'est précisément pourquoi cette offre existe. Aujourd'hui : kana, 200 leçons N5 à N3, SRS, JLPT blancs, IA. Bientôt : audio natif, kanji JLPT complet, mode conversation vocale, N2. Vous obtenez tout."],
              ["Comment annuler / me faire rembourser ?",
               "14 jours pour changer d'avis, remboursement intégral en 1 email. Au-delà : pas de remboursement, mais l'accès reste à vie."],
              ["Je paie comment ?",
               "Stripe Checkout : carte bancaire (Visa/MC/Amex), Apple Pay, Google Pay. Aucun stockage de vos données chez nous."],
            ].map(([q, a], i) => (
              <details key={i} className="bg-white rounded-xl p-5 border-2 border-[#1A2A40]/10 group" data-testid={`faq-${i}`}>
                <summary className="font-bold text-[#1A2A40] cursor-pointer list-none flex items-center justify-between">
                  {q}
                  <span className="text-[#E94A3F] group-open:rotate-45 transition-transform text-xl">+</span>
                </summary>
                <p className="text-[#4A5568] mt-3 leading-relaxed text-sm">{a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-[#1A2A40] py-24 text-center">
        <div className="max-w-3xl mx-auto px-6 sm:px-12">
          <h2 className="font-display font-black text-5xl sm:text-6xl tracking-tighter leading-[1] mb-6">
            Encore <span className="text-[#FFD166]">{remaining}</span> places.<br />
            <span className="italic font-light">Puis l'offre disparaît.</span>
          </h2>
          <p className="text-lg text-white/70 mb-10">
            49 € une fois. Le japonais à vie.
          </p>
          <button
            onClick={claim}
            disabled={loading || (status && !status.available) || user?.is_lifetime}
            data-testid="founder-cta-bottom"
            className="bg-[#E94A3F] text-white font-bold py-5 px-10 rounded-xl shadow-[6px_6px_0_0_#FFD166] hover-lift inline-flex items-center gap-2 text-lg disabled:opacity-50"
          >
            {user?.is_founder ? "Déjà fondateur 👑" : status?.available ? "Réserver ma place" : "Trop tard"}
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Fondateur;
